import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";

import Header from "./header";
import Nav from "./nav";
import Footer from "./footer";
import Promotions from "./Promotions";
import HeroCarousel from "./HeroCarousel";
import CategoryGrid from "./CategoryGrid";
import Search from "./Search";

import one from "../assets/one.png";
import two from "../assets/two.png";
import together from "../assets/together.png";
import logo from "../assets/logo.jpg";

import { GetHomeBanners } from "../utils/homeBanner";

function HomePage() {
  const loggedUser = useSelector(
    (state) => state.user.logedUser
  );

  const location = useLocation();

  const [homeImages, setHomeImages] = useState([]);
  const [isLoadingBanners, setIsLoadingBanners] =
    useState(true);
  const [bannerError, setBannerError] = useState("");

  const isHomePage = location.pathname === "/";

  useEffect(() => {
    let isMounted = true;

    const fetchBanners = async () => {
      setIsLoadingBanners(true);
      setBannerError("");

      try {
        const data = await GetHomeBanners();

        if (!isMounted) {
          return;
        }

        const validImages = Array.isArray(data)
          ? data
            .map((item) => item?.image)
            .filter(Boolean)
          : [];

        setHomeImages(validImages);
      } catch (error) {
        console.error(
          "שגיאה בטעינת תמונות דף הבית:",
          error
        );

        if (isMounted) {
          setBannerError(
            "לא הצלחנו לטעון כרגע את תמונות דף הבית. מוצגות תמונות חלופיות."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoadingBanners(false);
        }
      }
    };

    fetchBanners();

    return () => {
      isMounted = false;
    };
  }, []);

  const hotspots = [
    {
      id: "maagal",
      label: "מעגל השנה",
      x: 3,
      y: 12,
      w: 30,
      h: 32,
      to: "/categories/maagal",
    },
    {
      id: "arts",
      label: "חומרי יצירה",
      x: 35,
      y: 12,
      w: 30,
      h: 32,
      to: "/categories/arts",
    },
    {
      id: "writing",
      label: "כלי כתיבה",
      x: 67,
      y: 12,
      w: 30,
      h: 32,
      to: "/categories/writing",
    },
    {
      id: "gifts",
      label: "מתנות ועיצובים",
      x: 35,
      y: 56,
      w: 30,
      h: 32,
      to: "/categories/gifts",
    },
    {
      id: "games",
      label: "משחקים",
      x: 67,
      y: 56,
      w: 30,
      h: 32,
      to: "/categories/games",
    },
  ];

  const carouselImages =
    homeImages.length > 0
      ? homeImages
      : [one, two];

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
      }}
    >
      {/* קישור מעבר ישיר לתוכן עבור משתמשי מקלדת */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "fixed",
          top: 8,
          right: 8,
          zIndex: 9999,
          padding: "10px 16px",
          backgroundColor: "#ffffff",
          color: "#000000",
          border: "2px solid #D81B60",
          borderRadius: "6px",
          fontWeight: 700,
          textDecoration: "none",
          transform: "translateY(-150%)",
          transition: "transform 0.2s ease",

          "&:focus": {
            transform: "translateY(0)",
            outline: "3px solid #000000",
            outlineOffset: "3px",
          },
        }}
      >
        מעבר לתוכן הראשי
      </Box>

      {/* אזור עליון: לוגו, ניווט וחיפוש */}
      <Box
        component="header"
        aria-label="כותרת האתר"
        sx={{
          width: "100%",
          backgroundColor: "#ffffff",
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                md: "row",
              },
              gap: {
                xs: 2,
                md: 3,
              },
              alignItems: "center",
              justifyContent: "center",
              py: {
                xs: 2,
                md: 1,
              },
            }}
          >
            <Box
              component="a"
              href="/"
              aria-label="שיינ'ס סטוק – מעבר לדף הבית"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: "4px",
                textDecoration: "none",

                "&:focus-visible": {
                  outline: "3px solid #D81B60",
                  outlineOffset: "4px",
                },
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="שיינ'ס סטוק"
                sx={{
                  width: {
                    xs: 100,
                    md: 180,
                  },
                  height: "auto",
                  display: "block",
                }}
              />
            </Box>

            <Box
              component="nav"
              aria-label="ניווט ראשי"
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Nav />
            </Box>

            <Box
              component="section"
              role="search"
              aria-label="חיפוש מוצרים באתר"
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
              }}
            >
              <Search fromNavBar />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* הסרגל נמצא מחוץ לאזור העליון */}
      <Header />

      {/* תוכן ראשי */}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flexGrow: 1,

          "&:focus": {
            outline: "none",
          },

          "& a:focus-visible, & button:focus-visible, & input:focus-visible":
          {
            outline: "3px solid #D81B60",
            outlineOffset: "3px",
          },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            mt: 0,
          }}
        >
          {isHomePage && (
            <>
              {/* כותרת ראשית לקוראי מסך ולמבנה העמוד */}
              <Typography
                component="h1"
                sx={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  padding: 0,
                  margin: "-1px",
                  overflow: "hidden",
                  clip: "rect(0, 0, 0, 0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }}
              >
                שיינ'ס סטוק – מוצרי כתיבה,
                יצירה, משחקים ומתנות
              </Typography>

              {loggedUser && (
                <Box
                  component="section"
                  aria-label="ברכה למשתמש"
                  sx={{
                    px: {
                      xs: 2,
                      md: 10,
                    },
                    mt: 2,
                  }}
                >
                  <Typography
                    component="p"
                    variant="h5"
                    role="status"
                    aria-live="polite"
                    sx={{
                      fontSize: {
                        xs: "1.1rem",
                        md: "1.5rem",
                      },
                    }}
                  >
                    שלום {loggedUser.name}, ברוך הבא
                    לשיינ'ס סטוק!
                  </Typography>
                </Box>
              )}

              <Box
                component="section"
                aria-labelledby="promotions-title"
                sx={{
                  mt: 2,
                }}
              >
                <Typography
                  id="promotions-title"
                  component="h2"
                  sx={{
                    position: "absolute",
                    width: "1px",
                    height: "1px",
                    padding: 0,
                    margin: "-1px",
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    whiteSpace: "nowrap",
                    border: 0,
                  }}
                >
                  מבצעים והטבות
                </Typography>

                <Promotions />
              </Box>

              <Box
                component="section"
                aria-labelledby="home-banners-title"
                aria-busy={isLoadingBanners}
                sx={{
                  mt: 2,
                }}
              >
                <Typography
                  id="home-banners-title"
                  component="h2"
                  sx={{
                    position: "absolute",
                    width: "1px",
                    height: "1px",
                    padding: 0,
                    margin: "-1px",
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    whiteSpace: "nowrap",
                    border: 0,
                  }}
                >
                  מבצעים ומוצרים מומלצים
                </Typography>

                {bannerError && (
                  <Alert
                    severity="warning"
                    role="status"
                    sx={{
                      mb: 2,
                    }}
                  >
                    {bannerError}
                  </Alert>
                )}

                {isLoadingBanners ? (
                  <Box
                    role="status"
                    aria-live="polite"
                    sx={{
                      minHeight: {
                        xs: 250,
                        md: 500,
                      },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <CircularProgress
                      aria-hidden="true"
                    />

                    <Typography component="p">
                      תמונות דף הבית נטענות
                    </Typography>
                  </Box>
                ) : (
                  <HeroCarousel
                    images={carouselImages}
                    interval={4000}
                    height={500}
                    maxWidth={1300}
                  />
                )}
              </Box>

              <Box
                component="section"
                aria-labelledby="categories-title"
                sx={{
                  mt: {
                    xs: 3,
                    md: 5,
                  },
                  mb: {
                    xs: 3,
                    md: 5,
                  },
                }}
              >
                <Typography
                  id="categories-title"
                  component="h2"
                  variant="h4"
                  align="center"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    fontSize: {
                      xs: "1.5rem",
                      md: "2rem",
                    },
                  }}
                >
                  קטגוריות המוצרים שלנו
                </Typography>

                <CategoryGrid
                  src={together}
                  hotspots={hotspots}
                  ratio={0.5}
                  maxWidth={1200}
                />
              </Box>
            </>
          )}

          {/* תוכן של עמודים פנימיים */}
          <Outlet />
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}

export default HomePage;